// Расхождения состава: где итог по товарным строкам не равен сумме сделки. ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ЭКРАН, А НЕ ВКЛАДКА В «ОТЧЁТАХ РУКОВОДИТЕЛЯ». Дверь другая, и это не
// мелочь. «Отчёты руководителя» открыты группе разделов 'отчёты' — админ, бухгалтерия и
// руководитель (web/bff/rights.py). А эта ручка требует ВСЮ базу: BFF зовёт
// `rights.проверить_чтение_свода`, и роль 'head' со своей зоной получает 403. Вкладка,
// которая у руководителя всегда отказ, — это обещание, которого экран не держит.
//
// ЧТО ЭКРАН ОБЯЗАН СКАЗАТЬ ПЕРВЫМ. «8 693 сделки расходятся на 2,79 млрд» (28.07.2026) —
// цифра, которой пользоваться нельзя: руководитель либо пугается зря, либо перестаёт верить
// экрану. Из этих 8 693 (замер боевой базы 28.07.2026) чинить надо 702, а 7 058 — не ошибка:
// сумму старых сделок не заводили, работы и доставку в состав не вписывают, разница
// меньше 5 % — торг. Поэтому по умолчанию открыт `kind=error`, а не весь список.
//
// ПОЧЕМУ ПРИЧИНЫ РИСУЮТСЯ СТРОКАМИ ТЕКСТОМ ЯДРА, А НЕ ПЛИТКАМИ СО СВОИМИ ПОДПИСЯМИ.
// Формулировка причины живёт в ОДНОМ месте — в products_api.py (`ПРИЧИНЫ`), и приезжает
// в поле `why`. Свой словарь «код → короткая подпись» пришлось бы держать второй копией,
// и он молча опустел бы в день, когда в ядре появится одиннадцатая причина. Список причин
// экран тоже не знает: кнопки фильтра строятся из `summary.by_reason`, то есть из ответа.
//
// СВОДКА СЧИТАЕТСЯ БЕЗ УЧЁТА ФИЛЬТРА — так её отдаёт ядро, и так и надо. Она отвечает на
// вопрос «из чего сложились 2,79 млрд», и сузить её выбранным фильтром значило бы спрятать
// кучу, в которой лежит половина денег. Но человеку об этом надо сказать вслух, иначе
// плитки прочитаются как «итог по тому, что в таблице».
//
// ЗАПИСИ ЗДЕСЬ НЕТ И БЫТЬ НЕ МОЖЕТ. Всё, что этот экран нашёл, чинится в Bitrix, в самой
// сделке: на товарных строках висят бизнес-процессы, и правка из зеркала запускает их.
import { useSearchParams } from 'react-router-dom'

import { useRightsApi } from '../api/hooks'
import { НетДоступа, useРасхождения, useСправочникиСостава } from '../api/products'
import type {
  КучаРасхождений, ПричинаРасхождения, СводкаРасхождений, СтепеньРасхождения, СтрокаРасхождения,
} from '../api/products'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney } from '../lib/format'
import {
  ПОДПИСЬ_СТЕПЕНИ, ПОРЯДОК_СТЕПЕНЕЙ, классПричины, направлениеРазницы, сколькоСделок,
} from '../lib/products'

/** Значение `kind` в адресе, означающее «не фильтровать». Пустая строка не годится:
 *  её не отличить от «параметра нет», а «параметра нет» — это умолчание `error`. */
const ВСЕ = 'all'

export default function Mismatches() {
  const права = useRightsApi()

  // Дверь ТА ЖЕ, что у ручки, и проверяется по тому же признаку. `scoped('deals')` — это
  // ровно серверное `не зона.все` (web/bff/rights.py: 'deals' не входит в РАЗДЕЛЫ_БЕЗ_СУЖЕНИЯ),
  // то есть человек видит только свои сделки. Спрашивать свод у сервера в этом случае
  // незачем: ответ известен заранее — 403.
  //
  // Пока права НЕ пришли (`loaded === false`), не прячем ничего: догадка «наверное, нельзя»
  // показала бы отказ тому, кому раздел открыт, и он решил бы, что доступ отобрали. На бою
  // этого мига не бывает вовсе — RequireAuth ждёт тот же самый запрос /api/me и до отрисовки
  // страницы не пускает, — но проверка здесь всё равно нужна: экран рисуют и в тестах.
  //
  // Отказ мы всё равно ЖДЁМ и от сервера: это лишь способ не ходить за заведомым 403.
  // Единственная настоящая дверь — в web/bff/main.py, и она стоит независимо от этой.
  if (права.loaded && (права.denied('deals') || права.scoped('deals'))) {
    return (
      <>
        <div className="page-head"><h1>Расхождения состава</h1></div>
        <Отказ свой={права.scoped('deals')} основание={права.basis} />
      </>
    )
  }
  return <Список />
}

/**
 * Отказ по правам — объяснением, а не пустой таблицей.
 *
 * Пустая таблица здесь читалась бы как «расхождений нет», то есть как «в базе всё сходится».
 * Это худшая из возможных неправд на таком экране.
 */
function Отказ({ свой, основание }: { свой: boolean; основание: string }) {
  return (
    <div className="card">
      <div className="empty-title">Этот свод вам не открыт</div>
      <div className="empty-hint">
        {свой
          ? 'Расхождение состава с суммой считается по всем сделкам сразу — сузить его до'
            + ' вашей зоны нечем: это был бы ответ на другой вопрос, и экран не смог бы'
            + ' сказать, на какой именно. Раздел открыт тем, кому видна вся база.'
          : 'Раздел сделок закрыт вашей роли, а расхождения состава — это деньги сделок.'}
        {основание ? ` Ваша зона: ${основание}.` : ''}
      </div>
    </div>
  )
}

function Список() {
  const [sp, setSp] = useSearchParams()
  // Фильтр и страница живут в адресе, а не в состоянии: ссылку на «дубли состава» посылают
  // в чат ответственному, и она обязана открыться тем же списком.
  const выбор = sp.get('kind') ?? 'error'
  const порог = sp.get('min') ?? ''
  const старт = Math.max(0, Number(sp.get('start') ?? 0) || 0)

  // Значение `kind` в ядро уходит как есть, без проверки по списку допустимых: списка
  // причин фронт не знает и знать не должен (он приезжает в `summary.by_reason`). Правку
  // адреса руками ядро отобьёт своим 400 со списком допустимых значений, и QueryState
  // покажет этот текст — он точнее любого нашего.
  const q = useРасхождения({
    kind: выбор === ВСЕ ? undefined : (выбор as СтепеньРасхождения | ПричинаРасхождения),
    min: порог ? Number(порог) : undefined,
    start: старт || undefined,
  })
  // Справочник раздела — только ради свежести зеркала и знаменателя «из скольких сделок».
  // Экран на нём не держится: не пришёл — просто не показываем эту строку.
  const refs = useСправочникиСостава()

  const правка = (что: Record<string, string | null>, сброситьСтраницу = true) => {
    const n = new URLSearchParams(sp)
    for (const [k, v] of Object.entries(что)) {
      if (v) n.set(k, v)
      else n.delete(k)
    }
    if (сброситьСтраницу) n.delete('start')
    setSp(n, { replace: true })
  }

  const свод = q.data?.summary
  const строки = q.data?.rows ?? []
  const лимит = q.data?.limit ?? 50
  const всего = q.data?.total ?? 0
  const свежесть = refs.data?.synced?.age_min

  return (
    <>
      <div className="page-head">
        <h1>Расхождения состава</h1>
        <span className="spacer" />
        {typeof свежесть === 'number' && (
          <span className="hint">
            зеркало обновлено {свежесть < 1 ? 'только что' : `${Math.round(свежесть)} мин назад`}
          </span>
        )}
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Расхождение — не одна беда, а десять разных.</b> Итог по товарным строкам не
        сходится с суммой сделки у многих сделок, но большинство таких сделок чинить не
        надо: сумму старых сделок не заводили вовсе, работы и доставку в состав не
        вписывают, а разница меньше 5 % — это торг при выставлении. Поэтому список открыт
        на том, что <b>действительно заведено неверно</b>, а не на всех расхождениях сразу.
        Причину у каждой сделки называет ядро — тем же правилом, что и карточка сделки.
      </div>

      <Состояние isLoading={q.isLoading} error={q.error}>
        <>
          {свод && <Степени свод={свод} выбор={выбор} выбрать={(k) => правка({ kind: k })} />}
          {свод && (
            <Кучи свод={свод} выбор={выбор} выбрать={(k) => правка({ kind: k })}
                  сделокСоСоставом={refs.data?.counts.deals_with_rows} />
          )}

          <div className="card">
            <FilterBar id="mismatches" style={{ marginBottom: 12 }}>
              <div className="f">
                <label htmlFor="min">Разница не меньше, ₽</label>
                <input id="min" type="number" inputMode="numeric" min={0} value={порог}
                       placeholder="без порога"
                       onChange={(e) => правка({ min: e.target.value.trim() || null })} />
              </div>
              <div className="f" style={{ alignSelf: 'flex-end' }}>
                <button onClick={() => правка({ kind: null, min: null })}
                        disabled={выбор === 'error' && !порог}>
                  вернуть к ошибкам
                </button>
              </div>
              <span className="spacer" />
              <div className="hint" style={{ alignSelf: 'flex-end' }}>
                Показано: {сколькоСделок(всего)}
                {q.isFetching ? ' · обновляется…' : ''}
              </div>
            </FilterBar>

            {строки.length === 0
              ? (
                <div>
                  <div className="empty-title">По этому отбору сделок нет</div>
                  <div className="empty-hint">
                    Это ответ «ничего не нашлось по выбранной причине и порогу», а не
                    «в базе всё сходится»: счётчики выше посчитаны по всей базе.
                  </div>
                </div>
              )
              : <Таблица строки={строки} />}

            {всего > лимит && (
              <div className="pager">
                <button disabled={старт === 0 || q.isFetching}
                        onClick={() => правка({ start: String(Math.max(0, старт - лимит)) },
                                              false)}>
                  ← назад
                </button>
                <span className="muted">
                  {старт + 1}–{Math.min(старт + лимит, всего)} из {всего}
                </span>
                <button disabled={q.data?.next == null || q.isFetching}
                        onClick={() => правка({ start: String(q.data!.next!) }, false)}>
                  вперёд →
                </button>
              </div>
            )}

            <div className="hint" style={{ marginTop: 10 }}>
              Порядок — по величине расхождения, от большего к меньшему. Всё найденное
              чинится в Bitrix, в самой сделке: зеркало правку не примет и принять не
              должно — на товарных строках висят бизнес-процессы.
            </div>
          </div>
        </>
      </Состояние>
    </>
  )
}

/**
 * Обёртка над QueryState: 403 здесь не поломка, а нормальный ответ сервера, и выглядеть
 * он должен объяснением, а не строкой «Ошибка: forbidden». Тот же приём, что в Money.tsx.
 *
 * Проверка стоит ДО QueryState, а не внутри него: QueryState на любой ошибке возвращает
 * свою карточку и до детей не доходит вовсе.
 */
function Состояние({
  isLoading, error, children,
}: { isLoading: boolean; error: unknown; children: React.ReactNode }) {
  if (error instanceof НетДоступа) return <Отказ свой основание="" />
  return <QueryState isLoading={isLoading} error={error}>{children}</QueryState>
}

/**
 * Счётчики по степеням: чинить, посмотреть, не трогать.
 *
 * Считаны по ВСЕЙ базе, а не по выбранному фильтру — так их отдаёт ядро. Об этом сказано
 * подписью под плитками: без неё счётчик по всей базе рядом с таблицей на 50 строк
 * читается как «столько строк в таблице».
 */
function Степени({
  свод, выбор, выбрать,
}: { свод: СводкаРасхождений; выбор: string; выбрать: (k: string) => void }) {
  const по = new Map(свод.by_severity.map((s) => [s.severity, s]))
  return (
    <div className="card">
      <div className="tiles">
        {ПОРЯДОК_СТЕПЕНЕЙ.map((с) => {
          const к = по.get(с)
          const подпись = ПОДПИСЬ_СТЕПЕНИ[с]
          return (
            <div key={с} className={`tile${с === 'error' && (к?.deals ?? 0) > 0 ? ' hot' : ''}`}
                 title={подпись.что}>
              <div className="t-label">{подпись.плитка}</div>
              <div className="t-sum">{сколькоСделок(к?.deals ?? 0)}</div>
              <div className="t-label">{fmtMoney(к?.money ?? 0)}</div>
            </div>
          )
        })}
        <div className="tile" title="Все расхождения зеркала, из чего бы они ни сложились">
          <div className="t-label">Всего расходится</div>
          <div className="t-sum">{сколькоСделок(свод.deals)}</div>
          <div className="t-label">{fmtMoney(свод.money)}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
        {ПОРЯДОК_СТЕПЕНЕЙ.map((с) => (
          <button key={с} onClick={() => выбрать(с)} aria-pressed={выбор === с}
                  title={ПОДПИСЬ_СТЕПЕНИ[с].что}
                  style={{ fontWeight: выбор === с ? 600 : 400 }}>
            {ПОДПИСЬ_СТЕПЕНИ[с].плитка} · {по.get(с)?.deals ?? 0}
          </button>
        ))}
        <button onClick={() => выбрать(ВСЕ)} aria-pressed={выбор === ВСЕ}
                style={{ fontWeight: выбор === ВСЕ ? 600 : 400 }}>
          Все расхождения · {свод.deals}
        </button>
      </div>

      <div className="hint" style={{ marginTop: 10 }}>
        Эти счётчики посчитаны по всей базе и <b>не зависят от выбранного фильтра</b> —
        иначе куча, в которой лежит половина денег, выглядела бы несуществующей, стоило
        отфильтровать список. К таблице ниже относится только строка «Показано».
      </div>
    </div>
  )
}

/** Кучи по причинам — текстом ядра. Пустые показываем тоже: пропавшая строка читается
 *  как «такой причины не бывает», а не «сегодня таких сделок нет». */
function Кучи({
  свод, выбор, выбрать, сделокСоСоставом,
}: {
  свод: СводкаРасхождений
  выбор: string
  выбрать: (k: string) => void
  сделокСоСоставом?: number
}) {
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Из чего это сложилось</h2>
      <div className="hint" style={{ marginBottom: 10 }}>
        {сделокСоСоставом
          ? `Расходится ${сколькоСделок(свод.deals)} из ${сделокСоСоставом.toLocaleString('ru-RU')}`
            + ' сделок, у которых состав вообще заведён. '
          : ''}
        Задублированный состав — самая свежая настоящая ошибка из всех: подавляющее
        большинство таких сделок заведено за последний год, больше двух третей — за
        первое полугодие этого года.
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Причина</th>
              <th className="num nowrap">Сделок</th>
              <th className="num nowrap">Денег</th>
              <th className="nowrap">Что делать</th>
            </tr>
          </thead>
          <tbody>
            {свод.by_reason.map((к) => (
              <СтрокаКучи key={к.reason} куча={к} выбран={выбор === к.reason} выбрать={выбрать} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function СтрокаКучи({
  куча, выбран, выбрать,
}: { куча: КучаРасхождений; выбран: boolean; выбрать: (k: string) => void }) {
  return (
    <tr style={выбран ? { fontWeight: 600 } : undefined}>
      <td data-label="Причина">
        <span className={`badge ${классПричины(куча.severity)}`}>{куча.reason}</span>{' '}
        {куча.why}
      </td>
      <td className="num" data-label="Сделок">
        {куча.deals > 0
          ? (
            <button onClick={() => выбрать(куча.reason)} aria-pressed={выбран}>
              {куча.deals}
            </button>
          )
          // Нулевую кучу не делаем кнопкой: нажатие открыло бы пустую таблицу и заставило
          // человека решать, сломался фильтр или сделок правда нет.
          : <span className="muted">0</span>}
      </td>
      <td className="num" data-label="Денег">{fmtMoney(куча.money)}</td>
      <td className="nowrap" data-label="Что делать">
        <span className="muted">{ПОДПИСЬ_СТЕПЕНИ[куча.severity].плитка}</span>
      </td>
    </tr>
  )
}

function Таблица({ строки }: { строки: СтрокаРасхождения[] }) {
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Сделка</th>
            <th>Причина</th>
            <th className="num nowrap">Строк</th>
            <th className="num nowrap">По строкам</th>
            <th className="num nowrap">Сумма сделки</th>
            <th className="num nowrap">Разница</th>
            <th className="nowrap">Изменена</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => {
            const валюта = r.currency || 'RUB'
            return (
              <tr key={r.deal_id}>
                <td data-label="Сделка">
                  <EntityLink kind="deal" id={r.deal_id} title={r.title} />
                </td>
                <td data-label="Причина">
                  <span className={`badge ${классПричины(r.severity)}`}>{r.why}</span>
                </td>
                <td className="num" data-label="Строк">{r.lines}</td>
                <td className="num" data-label="По строкам">{fmtMoney(r.lines_sum, валюта)}</td>
                {/* Пустая сумма сделки — это НЕ ноль: у 4 863 сделок (на 28.07.2026) её
                    просто не заводили. fmtMoney(null) даёт «—», и подменять его нулём
                    здесь нельзя. */}
                <td className="num" data-label="Сумма сделки">
                  {fmtMoney(r.opportunity, валюта)}
                </td>
                <td className="num" data-label="Разница" title={направлениеРазницы(r.delta)}>
                  {fmtMoney(r.delta, валюта)}
                </td>
                <td className="nowrap" data-label="Изменена">{fmtDate(r.date_modify)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
